import { useCallback, useMemo, useRef } from "react";

/**
 * Ordering for a component that issues the same GET from more than one caller (sc-100769).
 *
 * The runs tabs fetch from three places — mount, the post-delete reload, and the poll tick — and
 * `usePollWhile`'s overlap guard serializes only the ticks *it* starts, so responses can arrive out of
 * order. Two distinct things need ordering, and they need *different* rules:
 *
 * - **Who owns the loading flag** — the newest caller to have *begun*. Whoever started last is the one
 *   the operator is waiting on, so it must release the skeleton whether it succeeds or fails; otherwise a
 *   superseded visible load skips its release and a failing poll never makes one, and the table stays
 *   hidden with correct data behind it.
 * - **Whose data may commit** — the newest response to have *succeeded*. Claiming the commit at `begin()`
 *   was wrong: a tick that begins after a post-delete reload and then FAILS would block that reload from
 *   ever committing, so the table came back showing the row the server had already deleted. A request may
 *   commit unless something newer has already committed. [code review r5]
 *
 * Deliberately not a single-flight guard: these GETs are cheap and idempotent, and suppressing the
 * post-delete reload because a tick is outstanding would show stale data. Ordering is the problem.
 */
export interface LatestOnly {
  /** Claim newest-begun status; returns the token to check after awaiting. */
  begin: () => number;
  /** True while no later caller has begun — the loading-flag owner. */
  isCurrent: (token: number) => boolean;
  /** True if this response may commit (nothing newer has); records it as the newest commit. */
  claimCommit: (token: number) => boolean;
}

/** Returns a stable `LatestOnly` — see the interface above for the two rules and why they differ. */
export function useLatestOnly(): LatestOnly {
  const begunRef = useRef(0);
  const committedRef = useRef(0);

  const begin = useCallback(() => ++begunRef.current, []);
  const isCurrent = useCallback((token: number) => token === begunRef.current, []);
  const claimCommit = useCallback((token: number) => {
    if (token <= committedRef.current) return false;
    committedRef.current = token;
    return true;
  }, []);

  return useMemo(() => ({ begin, isCurrent, claimCommit }), [begin, isCurrent, claimCommit]);
}
